import * as React from "react"
import {
  UsersIcon,
  RadioIcon,
  PackageIcon,
  ClipboardListIcon,
  TruckIcon,
  CarIcon,
  PlugIcon,
  MessageSquareIcon,
  ReceiptIcon,
  PhoneIncomingIcon,
  BotIcon,
  TagsIcon,
  DollarSignIcon,
  Phone,
  MessageCircle,
  MailIcon,
  FileTextIcon,
  KeyIcon,
  DatabaseIcon,
  BuildingIcon,
  NewspaperIcon,
  SettingsIcon,
} from "lucide-react"
import { Link } from "@inertiajs/react"

import { NavGroup } from "@/components/nav-group"
import ProfileDropdown from "@/components/shadcn-studio/blocks/dropdown-profile"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import authService from "@/lib/auth"

interface User {
  id: number
  name: string
  email: string
  role: string
  admin: boolean
  avatar_url?: string | null
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user: User
}

export function AppSidebar({ user, ...props }: AppSidebarProps) {
  const { state, toggleSidebar } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const isOwner = user.role === 'owner'
  const isAdmin = user.role === 'admin' || isOwner
  const isDispatcher = user.role === 'dispatcher'
  const isAccountant = user.role === 'accountant'
  const isIntern = user.role === 'intern'
  const isSupply = user.role === 'supply'
  const isAdminOrDispatcher = isAdmin || isDispatcher

  // Notification counts state
  const [notificationCounts, setNotificationCounts] = React.useState({
    phone: 0,
    sms: 0,
    email: 0,
  })

  // Fetch notification counts
  React.useEffect(() => {
    const fetchCounts = async () => {
      // Only fetch if user is authenticated
      const token = authService.getToken()
      if (!token || authService.isTokenExpired()) {
        return
      }

      try {
        const response = await fetch('/notifications', {
          headers: {
            'Accept': 'application/json',
            ...authService.authHeaders()
          }
        })

        if (!response.ok) {
          return
        }

        const data = await response.json()

        if (data.counts_by_type) {
          setNotificationCounts({
            phone: data.counts_by_type.phone || 0,
            sms: data.counts_by_type.sms || 0,
            email: data.counts_by_type.email || 0,
          })
        }
      } catch (error) {
        // Silent fail - don't spam console with errors
      }
    }

    // Fetch initial counts
    fetchCounts()

    // Poll every 2 minutes
    const interval = setInterval(fetchCounts, 120000)

    return () => clearInterval(interval)
  }, [])

  const operationsItems = [
    // Accountants see read-only operations
    ...(isAccountant ? [
      {
        title: "Accounting",
        url: "/dispatch",
        icon: RadioIcon,
        activePattern: "/dispatch",
      },
      {
        title: "Customers",
        url: "/customers",
        icon: UsersIcon,
        activePattern: "/customers",
      },
      {
        title: "Drivers",
        url: "/drivers",
        icon: TruckIcon,
        activePattern: "/drivers",
      },
      {
        title: "Cars",
        url: "/cars",
        icon: CarIcon,
        activePattern: "/cars",
      },
    ] : []),
    ...(isIntern ? [
      {
        title: "Dispatch (Read only)",
        url: "/dispatch",
        icon: RadioIcon,
        activePattern: "/dispatch",
      },
      {
        title: "Customers (Read only)",
        url: "/customers",
        icon: UsersIcon,
        activePattern: "/customers",
      },
      {
        title: "Drivers (Read only)",
        url: "/drivers",
        icon: TruckIcon,
        activePattern: "/drivers",
      },
      {
        title: "Cars (Read only)",
        url: "/cars",
        icon: CarIcon,
        activePattern: "/cars",
      },
      {
        title: "Inventory (Read only)",
        url: "/inventory_logs",
        icon: PackageIcon,
        activePattern: "/inventory_logs",
      },
    ] : []),
    ...(isSupply ? [
      {
        title: "Daily Dispatch and Accounting (Read only)",
        url: "/dispatch",
        icon: RadioIcon,
        activePattern: "/dispatch",
      },
      {
        title: "Customers (Read only)",
        url: "/customers",
        icon: UsersIcon,
        activePattern: "/customers",
      },
      {
        title: "Drivers",
        url: "/drivers",
        icon: TruckIcon,
        activePattern: "/drivers",
      },
      {
        title: "Cars",
        url: "/cars",
        icon: CarIcon,
        activePattern: "/cars",
      },
      {
        title: "Inventory",
        url: "/inventory_logs",
        icon: PackageIcon,
        activePattern: "/inventory_logs",
      },
    ] : []),
    // Dispatchers see operations
    ...(isDispatcher ? [
      {
        title: "Dispatch",
        url: "/dispatch",
        icon: RadioIcon,
        activePattern: "/dispatch",
      },
      {
        title: "Customers",
        url: "/customers",
        icon: UsersIcon,
        activePattern: "/customers",
      },
      {
        title: "Drivers",
        url: "/drivers",
        icon: TruckIcon,
        activePattern: "/drivers",
      },
      {
        title: "Cars",
        url: "/cars",
        icon: CarIcon,
        activePattern: "/cars",
      },
      {
        title: "Inventory",
        url: "/inventory_logs",
        icon: PackageIcon,
        activePattern: "/inventory_logs",
      },
    ] : []),
    // Admins see everything
    ...(isAdmin ? [
      {
        title: "Dispatch",
        url: "/dispatch",
        icon: RadioIcon,
        activePattern: "/dispatch",
      },
      {
        title: "Customers",
        url: "/customers",
        icon: UsersIcon,
        activePattern: "/customers",
      },
      {
        title: "Drivers",
        url: "/drivers",
        icon: TruckIcon,
        activePattern: "/drivers",
      },
      {
        title: "Cars",
        url: "/cars",
        icon: CarIcon,
        activePattern: "/cars",
      },
      {
        title: "Inventory",
        url: "/inventory_logs",
        icon: PackageIcon,
        activePattern: "/inventory_logs",
      },
    ] : []),
  ]

  // Calculate total notification count
  const totalNotifications = notificationCounts.phone + notificationCounts.sms + notificationCounts.email

  const communicationItems = isAdminOrDispatcher ? [
    {
      title: "Phone",
      url: "/communications?tab=phone",
      icon: Phone,
      activePattern: "/communications?tab=phone",
      badge: notificationCounts.phone,
    },
    {
      title: "SMS",
      url: "/communications?tab=sms",
      icon: MessageCircle,
      activePattern: "/communications?tab=sms",
      badge: notificationCounts.sms,
    },
    {
      title: "Email",
      url: "/communications?tab=email",
      icon: MailIcon,
      activePattern: "/communications?tab=email",
      badge: notificationCounts.email,
    },
    {
      title: "AI Communication",
      url: "/ai_communication",
      icon: BotIcon,
      activePattern: "/ai_communication",
    },
  ] : isAccountant ? [
    {
      title: "Phone",
      url: "/communications?tab=phone",
      icon: Phone,
      activePattern: "/communications?tab=phone",
      badge: notificationCounts.phone,
    },
    {
      title: "SMS",
      url: "/communications?tab=sms",
      icon: MessageCircle,
      activePattern: "/communications?tab=sms",
      badge: notificationCounts.sms,
    },
    {
      title: "Email (Read only)",
      url: "/communications?tab=email",
      icon: MailIcon,
      activePattern: "/communications?tab=email",
      badge: notificationCounts.email,
    },
    {
      title: "AI Communication (Read only)",
      url: "/ai_communication",
      icon: BotIcon,
      activePattern: "/ai_communication",
    },
  ] : []

  const salesItems = isAdminOrDispatcher ? [
    {
      title: "Leads",
      url: "/leads",
      icon: PhoneIncomingIcon,
      activePattern: "/leads",
    },
  ] : (isAccountant || isIntern) ? [
    {
      title: "Leads (Read only)",
      url: "/leads",
      icon: PhoneIncomingIcon,
      activePattern: "/leads",
    },
  ] : []

  const expensesItems = isAdmin ? [
    {
      title: "Invoices",
      url: "/invoices",
      icon: ReceiptIcon,
      activePattern: "/invoices",
    },
    {
      title: "Driver Performance",
      url: "/drivers/weekly_performance",
      icon: ClipboardListIcon,
      activePattern: "/drivers/weekly_performance",
    },
    {
      title: "Accounting Unit",
      url: "/driver_payments",
      icon: DollarSignIcon,
      activePattern: "/driver_payments",
    },
  ] : isDispatcher ? [
    {
      title: "Invoices (Read only)",
      url: "/invoices",
      icon: ReceiptIcon,
      activePattern: "/invoices",
    },
    {
      title: "Driver Performance",
      url: "/drivers/weekly_performance",
      icon: ClipboardListIcon,
      activePattern: "/drivers/weekly_performance",
    },
  ] : isAccountant ? [
    {
      title: "Invoices",
      url: "/invoices",
      icon: ReceiptIcon,
      activePattern: "/invoices",
    },
    {
      title: "Driver Performance (Read only)",
      url: "/drivers/weekly_performance",
      icon: ClipboardListIcon,
      activePattern: "/drivers/weekly_performance",
    },
    {
      title: "Accounting Unit",
      url: "/driver_payments",
      icon: DollarSignIcon,
      activePattern: "/driver_payments",
    },
  ] : isIntern ? [
    {
      title: "Invoices (Read only)",
      url: "/invoices",
      icon: ReceiptIcon,
      activePattern: "/invoices",
    },
    {
      title: "Driver Performance (Read only)",
      url: "/drivers/weekly_performance",
      icon: ClipboardListIcon,
      activePattern: "/drivers/weekly_performance",
    },
    {
      title: "Accounting Unit (Read only)",
      url: "/driver_payments",
      icon: DollarSignIcon,
      activePattern: "/driver_payments",
    },
  ] : isSupply ? [
    {
      title: "Driver Performance (Read only)",
      url: "/drivers/weekly_performance",
      icon: ClipboardListIcon,
      activePattern: "/drivers/weekly_performance",
    },
  ] : []

  const aiAgentsItems = isAdminOrDispatcher ? [
    {
      title: "AI Agents",
      url: "/ai_agents",
      icon: BotIcon,
      activePattern: "/ai_agents",
    },
  ] : []

  const newsItems = [
    {
      title: "News",
      url: "/news",
      icon: NewspaperIcon,
      activePattern: "/news",
    },
  ]

  const configurationItems = isAdmin ? [
    {
      title: "Services",
      url: "/admin/services",
      icon: TagsIcon,
      activePattern: "/admin/services",
    },
    {
      title: "Integrations",
      url: "/integrations",
      icon: PlugIcon,
      activePattern: "/integrations",
    },
  ] : []

  const usersItems = isAdmin ? [
    {
      title: "Users & Permissions",
      url: "/admin/users_management",
      icon: UsersIcon,
      activePattern: "/admin/users",
    },
  ] : []

  const administrationItems = isAdmin ? [
    {
      title: "Logs",
      url: "/admin/logs",
      icon: FileTextIcon,
      activePattern: "/admin/logs",
    },
  ] : []

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex h-10 items-center px-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2">
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleSidebar}
                  className="flex items-center justify-center rounded-md p-1 hover:bg-sidebar-accent transition-colors"
                >
                  <span className="text-lg font-bold text-blue-600">P</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Open sidebar</TooltipContent>
            </Tooltip>
          ) : (
            <>
              <Link
                href="/dispatch"
                className="flex flex-1 items-center overflow-hidden"
              >
                <img
                  src="/logo.png"
                  alt="Premium Detailing & Printing"
                  className="h-8 w-auto object-contain"
                />
              </Link>
              <SidebarTrigger className="[&_svg]:size-5 shrink-0" />
            </>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        {operationsItems.length > 0 && <NavGroup title="Operations" items={operationsItems} />}
        {expensesItems.length > 0 && <NavGroup title="Expenses" items={expensesItems} />}
        {salesItems.length > 0 && <NavGroup title="Sales" items={salesItems} />}
        {aiAgentsItems.length > 0 && <NavGroup title="AI Agents" items={aiAgentsItems} />}
        {communicationItems.length > 0 && (
          <NavGroup
            title="Communication"
            items={communicationItems}
            totalBadge={totalNotifications}
            collapsedIcon={MessageSquareIcon}
            collapsedUrl="/communications"
          />
        )}
        {newsItems.length > 0 && <NavGroup title="News" items={newsItems} />}
        {configurationItems.length > 0 && <NavGroup title="Configuration" items={configurationItems} />}
        {usersItems.length > 0 && <NavGroup title="Users" items={usersItems} />}
        {administrationItems.length > 0 && <NavGroup title="Administration" items={administrationItems} />}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <ProfileDropdown
              user={user}
              align="start"
              trigger={
                <SidebarMenuButton size="lg" className="w-full">
                  <Avatar className="size-7 rounded-md">
                    <AvatarImage src={user.avatar_url || undefined} alt={user.name} />
                    <AvatarFallback className="rounded-md text-xs">{user.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-left leading-tight">
                    <span className="text-sm font-medium truncate">{user.name}</span>
                    <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                  </div>
                </SidebarMenuButton>
              }
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
